import { useEffect, useState } from 'react';

type GestureDataPoint = { x: number; y: number };
type GestureDataSet = GestureDataPoint[];

export const useScrollingHandDetection = () => {
  const [dataSets, setDataSets] = useState<GestureDataSet[]>([]);
  const [guesses, setGuesses] = useState<('L' | 'R')[]>([]);

  const addDataSet = (dataSet: GestureDataSet) => {
    setDataSets((previousDataSets) => [...previousDataSets, dataSet]);
  };

  const addGuess = (guess: 'L' | 'R') => {
    setGuesses((previousGuesses) => [...previousGuesses, guess]);
  };

  const resetData = () => {
    setDataSets([]);
    setGuesses([]);
  };

  useEffect(() => {
    let touch: { x: number; y: number }[] = [];

    const touchStartHandler = (event: TouchEvent) => {
      const { clientX, clientY } = event.touches[0] || event.changedTouches[0];
      touch.push({ x: Math.round(clientX), y: Math.round(clientY) });
    };
    const touchMoveHandler = (event: TouchEvent) => {
      const { clientX, clientY } = event.touches[0] || event.changedTouches[0];
      touch.push({ x: Math.round(clientX), y: Math.round(clientY) });
    };
    const touchEndHandler = (event: TouchEvent) => {
      const { clientX, clientY } = event.touches[0] || event.changedTouches[0];
      touch.push({ x: Math.round(clientX), y: Math.round(clientY) });

      if (touch.length < 4) {
        touch = [];
        return;
      }

      addDataSet(touch);

      const firstTouchPoint = touch[0];
      const lastTouchPoint = touch[touch.length - 1];

      if (firstTouchPoint.y > lastTouchPoint.y) {
        if (firstTouchPoint.x < lastTouchPoint.x) {
          addGuess('R');
        } else {
          addGuess('L');
        }
      } else {
        if (firstTouchPoint.x > lastTouchPoint.x) {
          addGuess('R');
        } else {
          addGuess('L');
        }
      }

      touch = [];
    };

    addEventListener('touchstart', touchStartHandler);
    addEventListener('touchmove', touchMoveHandler);
    addEventListener('touchend', touchEndHandler);

    return () => {
      removeEventListener('touchstart', touchStartHandler);
      removeEventListener('touchmove', touchMoveHandler);
      removeEventListener('touchend', touchEndHandler);
    };
  }, []);

  const rightCount = guesses.filter((guess) => guess === 'R').length;
  const leftCount = guesses.filter((guess) => guess === 'L').length;

  const finalGuess: 'R' | 'L' | undefined =
    guesses.length === 0 ? undefined : rightCount > leftCount ? 'R' : 'L';
  const finalGuessCount = rightCount > leftCount ? rightCount : leftCount;

  const confidence =
    guesses.length === 0 ? 0 : (100 / guesses.length) * finalGuessCount;

  const confidenceRange = 100 / guesses.length;

  let confidenceLow =
    guesses.length === 0 ? 0 : confidence - confidenceRange / 2;
  let confidenceHigh =
    guesses.length === 0 ? 0 : confidence + confidenceRange / 2;

  if (confidenceLow < 0) {
    confidenceLow = 0;
  }

  if (confidenceHigh > 100) {
    confidenceHigh = 100;
  }

  return {
    guesses,
    resetData,
    dataSets,
    confidence,
    confidenceLow,
    confidenceHigh,
    finalGuess,
  };
};
